import type { TooltipProps } from '@unifyapps/ui/components/Tooltip';
import { Tooltip, TooltipModes } from '@unifyapps/ui/components/Tooltip';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { forwardRef, lazy, memo, Suspense, useEffect, useMemo, useState } from 'react';
import { getCommonMarkdownComponents } from './MarkdownComponents';

function loadTooltipMarkdownContentModule() {
  return import(/* webpackChunkName: "tooltip-markdown-content" */ './TooltipMarkdownContent');
}

const LazyTooltipMarkdownContent = lazy(loadTooltipMarkdownContentModule);

// Module-level flag so only the first TooltipWithMarkdown instance mounts the prewarm subtree.
let chunkPrewarmed = false;

type TooltipWithMarkdownProps = Omit<TooltipProps, 'description'> & {
  codeHighlighterMode?: 'dark' | 'light';
  description?: string;
  inlineCodeClassName?: string;
};

const BASE_SLOT_PROPS: TooltipProps['slotProps'] = {
  rootClassName: '!bg-solid-primary max-w-[400px]',
  arrowClassName: 'dark:before:!border-[var(--bg-solid-primary)]',
};

const TooltipWithMarkdown = forwardRef<HTMLDivElement, TooltipWithMarkdownProps>(
  function TooltipWithMarkdown(props, ref) {
    const {
      className,
      enterDelay = 0,
      description,
      disabled,
      inlineCodeClassName,
      mode,
      slotProps,
      ...rest
    } = props;

    const adaptedSlotProps = useMemo(
      () => ({
        ...BASE_SLOT_PROPS,
        ...slotProps,
        rootClassName: slowCn(BASE_SLOT_PROPS.rootClassName, slotProps?.rootClassName),
      }),
      [slotProps],
    );

    const markdownComponents = useMemo(
      () =>
        // If tooltip is in dark mode, use light mode for code highlighter and vice versa
        getCommonMarkdownComponents({
          codeHighlighterMode: mode === TooltipModes.DARK ? 'light' : 'dark',
          inlineCodeClassName,
        }),
      [inlineCodeClassName, mode],
    );

    const tooltipContent = useMemo(() => {
      if (!description) return null;

      return (
        <Suspense
          // render plaintext fallback so Popper calculates correct position while the markdown chunk loads
          fallback={
            <div className="prose prose-p:text-xs prose-p:text-utility-gray-200">
              <p className={className}>{description}</p>
            </div>
          }
        >
          <LazyTooltipMarkdownContent
            className={className}
            components={markdownComponents}
            description={description}
          />
        </Suspense>
      );
    }, [description, className, markdownComponents]);

    const [needsPrewarm] = useState(() => !chunkPrewarmed);

    useEffect(() => {
      chunkPrewarmed = true;
    }, []);

    return (
      <>
        {needsPrewarm ? (
          // Mount lazy markdown in a hidden subtree once so the chunk loads and Suspense resolves before any tooltip opens.
          <div aria-hidden hidden>
            <Suspense fallback={null}>
              <LazyTooltipMarkdownContent
                className={className}
                components={markdownComponents}
                description=" "
              />
            </Suspense>
          </div>
        ) : null}
        <Tooltip
          {...rest}
          description={disabled ? null : tooltipContent}
          enterDelay={enterDelay}
          mode={mode}
          ref={ref}
          slotProps={adaptedSlotProps}
        />
      </>
    );
  },
);

TooltipWithMarkdown.displayName = 'TooltipWithMarkdown';

export default memo(TooltipWithMarkdown);
